import { CartAddEvent, ThemeEvents } from '@theme/events';

// One transaction across both cart surfaces: no second click can enqueue a duplicate add.
const singlecapState = { busy: false, unresolved: null, cart: null, instances: new Set() };
const SINGLECAP_SOURCE = 'sc-cart-singlecaps';

function isSinglecapCart(cart) { return Boolean(cart && Array.isArray(cart.items) && Number.isFinite(cart.item_count)); }
function singlecapSubtotal(cart) {
  return (cart?.items || []).reduce((total, item) => {
    const properties = item.properties || {};
    return item.gift_card || properties._club_reward || properties._bf_gift || !(item.final_line_price > 0)
      ? total : total + item.final_line_price;
  }, 0);
}
function singlecapQuantity(cart, variantId) {
  return (cart?.items || []).reduce((sum, item) => sum + (String(item.variant_id || item.id) === String(variantId) ? Number(item.quantity) || 0 : 0), 0);
}
function singlecapRank(cards, cart, currency) {
  const present = new Set((cart?.items || []).map(item => String(item.product_id)));
  const gap = currency === 'EUR' ? Math.max(0, 10000 - singlecapSubtotal(cart)) : 0;
  return cards.map((card, index) => ({ card, index, distance: gap ? Math.abs((Number(card.dataset.price) || 0) - gap) : 0 }))
    .filter(({ card }) => !present.has(String(card.dataset.productId)))
    .sort((a, b) => a.distance - b.distance || a.index - b.index)
    .map(entry => entry.card);
}
function singlecapCartURL() {
  const url = new URL(Theme.routes.cart_url || `${window.Shopify?.routes?.root || '/'}cart`, window.location.origin);
  url.pathname = url.pathname.replace(/\.js$/, '').replace(/\/$/, '') + '.js';
  url.search = '';
  return url.href;
}
async function readSinglecapCart() {
  const response = await fetch(singlecapCartURL(), { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
  if (!response.ok) throw new Error('Cart read failed');
  const cart = await response.json();
  if (!isSinglecapCart(cart)) throw new Error('Invalid cart response');
  singlecapState.cart = cart;
  return cart;
}
function isSinglecapCartBusy() {
  return Boolean(window.__simpocubicClubRewardBusy || Array.from(document.querySelectorAll('cart-items-component')).some(element =>
    element.inert || element.getAttribute('aria-busy') === 'true' || element.classList.contains('cart-items-disabled')));
}
function lockSinglecapCartUI() {
  const previous = Array.from(document.querySelectorAll('cart-items-component')).map(element => ({
    element, inert: element.inert, busy: element.getAttribute('aria-busy'), disabled: element.classList.contains('cart-items-disabled'),
  }));
  for (const { element } of previous) {
    // This existing native class is also the BF checkout pipeline's waitForCartIdle signal.
    element.classList.add('cart-items-disabled');
    element.inert = true;
    element.setAttribute('aria-busy', 'true');
  }
  return () => {
    for (const { element, inert, busy, disabled } of previous) {
      element.inert = inert;
      if (busy === null) element.removeAttribute('aria-busy'); else element.setAttribute('aria-busy', busy);
      element.classList.toggle('cart-items-disabled', disabled);
    }
  };
}
function notifySinglecapInstances() {
  for (const instance of singlecapState.instances) instance.syncButtons();
}

class SinglecapRecommendations extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    try { this.config = JSON.parse(this.querySelector('[data-sc-cap-config]')?.textContent || '{}'); }
    catch (_) { return; }
    this._connected = true;
    this.cards = Array.from(this.querySelectorAll('[data-sc-cap-card]'));
    this.text = this.config.text || {};
    this.limit = Math.max(1, Math.min(4, Number(this.config.limit) || (this.config.context === 'drawer' ? 2 : 4)));
    this._click = event => this.handleClick(event);
    this._change = event => this.handleChange(event);
    this._cartUpdate = event => this.handleCartUpdate(event);
    this.addEventListener('click', this._click);
    this.addEventListener('change', this._change);
    document.addEventListener(ThemeEvents.cartUpdate, this._cartUpdate);
    singlecapState.instances.add(this);
    const cart = isSinglecapCart(this.config.initialCart) ? this.config.initialCart : singlecapState.cart;
    if (cart) singlecapState.cart = cart;
    this.renderCart(cart);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._click);
    this.removeEventListener('change', this._change);
    document.removeEventListener(ThemeEvents.cartUpdate, this._cartUpdate);
    singlecapState.instances.delete(this);
    this._connected = false;
  }

  setStatus(message, isError = false) {
    const status = this.querySelector('[data-sc-cap-status]');
    if (status) {
      status.textContent = message || '';
      status.dataset.error = String(isError);
    }
  }

  renderCart(cart) {
    const ranked = singlecapRank(this.cards, cart, cart?.currency || this.config.currency);
    const shown = new Set(ranked.slice(0, this.limit));
    const list = this.querySelector('[data-sc-cap-list]');
    for (const card of this.cards) card.hidden = !shown.has(card);
    // Stable original indices break ties; appending only on cart updates avoids jumps during selection.
    if (list) for (const card of ranked) list.append(card);
    this.hidden = ranked.length === 0;
    this.syncButtons();
  }

  syncButtons() {
    this.setAttribute('aria-busy', String(singlecapState.busy));
    for (const card of this.cards || []) {
      const button = card.querySelector('[data-sc-cap-add]');
      const select = card.querySelector('[data-sc-cap-select]');
      if (select) select.disabled = singlecapState.busy;
      if (!button) continue;
      const selected = Boolean(select ? select.value : card.dataset.variantId);
      button.disabled = singlecapState.busy || !selected;
      // An uncertain operation never turns another click into a new POST.
      button.textContent = singlecapState.unresolved || button.dataset.retry === 'true' ? this.text.retry : this.text.add;
      if (singlecapState.busy && card === this._activeCard) button.textContent = singlecapState.unresolved ? (this.text.checking || this.text.adding) : this.text.adding;
    }
  }

  handleChange(event) {
    const select = event.target.closest?.('[data-sc-cap-select]');
    if (!select || !this.contains(select) || singlecapState.busy || select.closest('[inert], [aria-busy="true"]')) return;
    const card = select.closest('[data-sc-cap-card]');
    const option = select.selectedOptions?.[0];
    if (!card || !option || !select.value) { this.syncButtons(); return; }
    card.dataset.variantId = select.value;
    card.dataset.price = option.dataset.price || card.dataset.price;
    const price = card.querySelector('[data-sc-cap-price]');
    if (price && option.dataset.money) price.textContent = option.dataset.money;
    const image = card.querySelector('[data-sc-cap-image]');
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
    if (option.dataset.url) for (const link of card.querySelectorAll('[data-sc-cap-link]')) link.href = option.dataset.url;
    this.setStatus('');
    this.syncButtons();
  }

  handleCartUpdate(event) {
    const cart = event.detail?.resource;
    if (isSinglecapCart(cart)) {
      singlecapState.cart = cart;
      this.renderCart(cart);
    } else if (!this._refreshing && !singlecapState.busy) {
      this._refreshing = readSinglecapCart().then(fresh => { if (this.isConnected) this.renderCart(fresh); }).catch(() => {}).finally(() => { this._refreshing = null; });
    }
  }

  sectionIds() {
    const ids = new Set(this.config.sectionId ? [String(this.config.sectionId)] : []);
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
    }
    return Array.from(ids).slice(0, 5);
  }

  publishCart(cart, sections, variantId) {
    singlecapState.cart = cart;
    const context = this.config.context;
    const scrollContainer = context === 'drawer' ? this.closest('.cart-drawer__content') : null;
    const scrollTop = scrollContainer?.scrollTop;
    const restoreFocus = this.contains(document.activeElement);
    this.setStatus(this.text.added);
    document.dispatchEvent(new CartAddEvent(cart, SINGLECAP_SOURCE, {
      source: SINGLECAP_SOURCE, itemCount: cart?.item_count, variantId: String(variantId), sections: sections || {},
    }));
    // Native section morphs remain the source of truth. Restore only this cart surface's scroll/focus.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const selector = context === 'drawer' ? '.cart-drawer__content sc-cart-singlecaps' : '.cart-page sc-cart-singlecaps';
      const replacement = document.querySelector(selector);
      if (replacement) replacement.setStatus?.(this.text.added);
      if (restoreFocus) {
        const next = replacement?.querySelector('[data-sc-cap-card]:not([hidden]) [data-sc-cap-add]:not([disabled]), [data-sc-cap-card]:not([hidden]) [data-sc-cap-select]');
        next?.focus({ preventScroll: true });
      }
      const freshScroll = context === 'drawer' ? document.querySelector('.cart-drawer__content') : null;
      if (freshScroll && Number.isFinite(scrollTop)) freshScroll.scrollTop = scrollTop;
    }));
  }

  async reconcileUncertain() {
    const pending = singlecapState.unresolved;
    if (!pending) return;
    this.setStatus(this.text.checking || this.text.uncertain);
    const cart = await readSinglecapCart();
    if (singlecapQuantity(cart, pending.variantId) > pending.baseline) {
      singlecapState.unresolved = null;
      this.publishCart(cart, null, pending.variantId);
    } else {
      // A late server commit cannot be ruled out after a network failure. Never issue another add blindly.
      this.renderCart(cart);
      this.setStatus(this.text.uncertain || this.text.error, true);
    }
  }

  async handleClick(event) {
    const button = event.target.closest?.('[data-sc-cap-add]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    if (button.disabled || singlecapState.busy || isSinglecapCartBusy() || button.closest('[inert], [aria-busy="true"]')) return;
    const card = button.closest('[data-sc-cap-card]');
    const select = card?.querySelector('[data-sc-cap-select]');
    const variantId = select ? select.value : card?.dataset.variantId;
    if (!card || !/^\d+$/.test(String(variantId || ''))) { this.setStatus(this.text.choose, true); return; }
    singlecapState.busy = true;
    this._activeCard = card;
    notifySinglecapInstances();
    const unlockCart = lockSinglecapCartUI();
    let postStarted = false;
    let rejected = false;
    let baseline = 0;
    let responseSections = null;
    try {
      if (singlecapState.unresolved) { await this.reconcileUncertain(); return; }
      const before = await readSinglecapCart();
      // Club reward automation can begin independently of this UI; never race its mutation.
      if (window.__simpocubicClubRewardBusy) return;
      baseline = singlecapQuantity(before, variantId);
      if (before.items.some(item => String(item.product_id) === String(card.dataset.productId))) {
        this.publishCart(before, null, variantId);
        return;
      }
      const payload = { items: [{ id: Number(variantId), quantity: 1 }], sections: this.sectionIds(), sections_url: window.location.pathname };
      postStarted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload), signal: AbortSignal.timeout(15000),
      });
      rejected = response.status >= 400 && response.status < 500;
      const result = await response.json();
      if (!response.ok || result.status >= 400 || result.errors) {
        throw new Error('Cart add rejected');
      }
      responseSections = result.sections;
      const cart = await readSinglecapCart();
      if (singlecapQuantity(cart, variantId) <= baseline) throw new Error('Addition not yet confirmed');
      this.publishCart(cart, responseSections, variantId);
    } catch (_) {
      if (postStarted && !rejected) {
        singlecapState.unresolved = { variantId: String(variantId), baseline };
        try { await this.reconcileUncertain(); }
        catch (_) { this.setStatus(this.text.uncertain || this.text.error, true); }
      } else {
        button.dataset.retry = 'true';
        this.setStatus(this.text.error, true);
      }
    } finally {
      unlockCart();
      singlecapState.busy = false;
      this._activeCard = null;
      notifySinglecapInstances();
    }
  }
}

if (!customElements.get('sc-cart-singlecaps')) customElements.define('sc-cart-singlecaps', SinglecapRecommendations);
